import { useState, forwardRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { getBlockLots } from '../common/getBlockLots';
import { useNavigate, useParams } from 'react-router-dom';
import { getUseGroup } from '../common/getUseGroup';

interface GeneralInformationFormProps {
  formData: {
    ApplicationTitle?: string;
    ApplicantName?: string;
    ProjectContactName?: string;
    ProjectContactPhone?: string;
    BlockLot?: string;
    ProjectLocation?: string;
    AuthorizedEmail?: string; //  this
    ApplicationFee?: string;
    InspectionFee?: string;
    ConstructionSchedule?: string;
    PurposeType?: any;
    DescribeUse?: string;
    PropertyUseType?: string;
    PropertyType?: string;
    ApplicantType?: string;
    // ApplicationType?: string;
    PresentGroupType?: string;
    ProposedGroupType?: string;
  };
  handleSubmit: (val: any) => void;
  isButtonHide?: boolean;
  setIsButtonHide?: (flag: boolean) => void;
  isEdit?: boolean;
}

interface BlockLot {
  PropAddress: string;
  BlockLot: string;
  OwnerName?: string;
}
const GeneralInformationForm = forwardRef<any, GeneralInformationFormProps>(
  ({ formData, handleSubmit, isEdit }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { permit } = useParams();
    const [blockLotsOptions, setBlockLotsOptions] = useState([]);
    const [UseGroupOptions, setUseGroupOptions] = useState([]);
    const [UseGroupOptionsDescriptions, setUseGroupOptionsDescriptions] = useState([]);

    const handleChange = async (val: string | BlockLot, e: any) => {
      const { name } = e;
      if (name === 'BlockLot' && val !== '') {
        if (typeof val === 'object') {
          dispatch(updateFormData({ [name]: val?.BlockLot }));
          dispatch(updateFormData({ ['ProjectLocation']: val?.PropAddress }));
          dispatch(updateFormData({ ['ApplicantName']: val?.OwnerName }));
          dispatch(updateFormData({ ['OwnersName']: val?.OwnerName }));
        } else {
          const blockLotsData = await getBlockLots(val);
          setBlockLotsOptions(blockLotsData);
        }
        return;
      }
      // Handle Group Type fields (new logic for object-based options)
      if (name === 'PresentGroupType' || name === 'ProposedGroupType') {
        // For group type fields, val will be the GroupName string
        dispatch(updateFormData({ [name]: val }));
        return;
      }
      dispatch(updateFormData({ [name]: val }));
    };

    const allFields: Fields[] = [
      {
        fieldName: 'BlockLot',
        initialValue: formData?.BlockLot || '',
        fieldLabel: 'Block Lot',
        fieldType: 'autocomplete',
        heading: 'Project Information',
        options: blockLotsOptions,
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'ProjectLocation',
        initialValue: formData?.ProjectLocation || '',
        fieldLabel: 'Project Location/Property Address',
        fieldType: 'text',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'ApplicationTitle',
        initialValue: formData?.ApplicationTitle || '',
        fieldLabel: 'Application Title',
        fieldType: 'text',
        required: true,
        dependency: handleChange
      },
      // {
      //   fieldName: 'ApplicationType',
      //   initialValue: formData?.ApplicationType || '',
      //   fieldLabel: 'Application Type',
      //   fieldType: 'select',
      //   options: ['New Build', 'Demolition', 'Renovation/Existing Building'],
      //   required: true,
      //   disabled: isEdit,
      //   dependency: handleChange
      // },
      {
        fieldName: 'PurposeType',
        initialValue: formData?.PurposeType || [],
        fieldLabel: 'Purpose Type',
        fieldType: 'multiselect',
        options: [
          'New Building',
          'Demolition',
          'Renovation/Existing Building',
          'Minor Work',
          'Addition',
          'Repair',
          'Alteration',
          'Reconstruction',
          'Asbestos Abat. - Subch. 8',
          'Lead Hazard Abatement',
          'Redon Remediation',
          'Annual Permit'
        ],
        required: true,
        multiSelect: true,
        disabled: isEdit,
        dependency: handleChange
      },
      {
        fieldName: 'DescribeUse',
        initialValue: formData?.DescribeUse || '',
        fieldLabel: 'Describe Project Work',
        fieldType: 'note',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'PropertyType',
        initialValue: formData?.PropertyType || '',
        fieldLabel: 'Property Type',
        fieldType: 'select',
        options: [
          'Private',
          'Corporate',
          'Public',
          'Partnership',
          'Trust or estate'
        ],
        disabled: isEdit,
        heading: 'Property Details',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'PropertyUseType',
        initialValue: formData?.PropertyUseType || '',
        fieldLabel: 'Property Use Type',
        fieldType: 'select',
        options: [
          'Residential',
          'Commercial',
          'Industrial',
          'Agriculture',
          'Mixed-use'
        ],
        required: true,
        disabled: isEdit,
        dependency: handleChange
      },
      {
        fieldName: 'ApplicantName',
        initialValue: formData?.ApplicantName || '',
        fieldLabel: 'Applicant Name',
        fieldType: 'text',
        heading: 'Applicant Details',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'ApplicantType',
        initialValue: formData?.ApplicantType || '',
        fieldLabel: 'Applicant Type',
        fieldType: 'select',
        options: ['Owner', 'Agent', 'Contractor', 'Architect', 'Tenant/Lease'],
        required: true,
        disabled: isEdit,
        dependency: handleChange
      },
      {
        fieldName: 'PresentGroupType',
        initialValue: formData?.PresentGroupType || '',
        fieldLabel: 'Present Occupancy Group Type',
        fieldType: 'select',
        heading: 'Building Occupancy Classification',
        options: UseGroupOptions,
        descriptions: UseGroupOptionsDescriptions,
        required: true,
        dependency: handleChange,
        helpIcon: true, // Add this
        helpContent: `GROUP CLASSIFICATIONS:
- A1-A5: Assembly (theaters, restaurants, churches, stadiums, arenas)
- B: Business (offices, professional services, banks)
- E: Educational (schools, universities, daycare centers)
- F1-F2: Factory/Industrial (manufacturing, processing facilities)
- H1-H4: High Hazard (explosive, flammable, toxic materials)
- I1-I4: Institutional (hospitals, prisons, assisted living)
- M: Mercantile (retail stores, shopping centers, markets)
- R1-R5: Residential (single-family, multi-family, hotels, dormitories)
- S1-S2: Storage (warehouses, parking garages, aircraft hangars)
- U: Utility (carports, barns, greenhouses, towers)` // Add this
      },
      {
        fieldName: 'ProposedGroupType',
        initialValue: formData?.ProposedGroupType || '',
        fieldLabel: 'Proposed Occupancy Group Type',
        fieldType: 'select',
        options: UseGroupOptions,
        descriptions: UseGroupOptionsDescriptions,
        required: true,
        dependency: handleChange,
        helpIcon: true, // Add this
        helpContent: `GROUP CLASSIFICATIONS:
- A1-A5: Assembly (theaters, restaurants, churches, stadiums, arenas)
- B: Business (offices, professional services, banks)
- E: Educational (schools, universities, daycare centers)
- F1-F2: Factory/Industrial (manufacturing, processing facilities)
- H1-H4: High Hazard (explosive, flammable, toxic materials)
- I1-I4: Institutional (hospitals, prisons, assisted living)
- M: Mercantile (retail stores, shopping centers, markets)
- R1-R5: Residential (single-family, multi-family, hotels, dormitories)
- S1-S2: Storage (warehouses, parking garages, aircraft hangars)
- U: Utility (carports, barns, greenhouses, towers)`
      },
      {
        fieldName: 'ProjectContactName',
        initialValue: formData?.ProjectContactName || '',
        fieldLabel: 'Project Contact Name',
        fieldType: 'text',
        heading: 'Contact Information',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'ProjectContactPhone',
        initialValue: formData?.ProjectContactPhone || '',
        fieldLabel: 'Project Phone Number',
        fieldType: 'mobile',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'AuthorizedEmail',
        initialValue: formData?.AuthorizedEmail || '',
        fieldLabel: 'Authorized Email Address',
        fieldType: 'email',
        required: true,
        dependency: handleChange
      },
      {
        fieldName: 'ConstructionSchedule',
        initialValue: formData?.ConstructionSchedule || '',
        fieldLabel: 'Construction Schedule',
        fieldType: 'date',
        heading: 'Timeline',
        required: true,
        dependency: (val: string) =>
          dispatch(updateFormData({ ConstructionSchedule: val }))
      }
    ];

    // Fetch all use groups
    useEffect(() => {
      const fetchUseGroups = async () => {
        const [UseGroupName, UseGroupDescription] = await getUseGroup();
        setUseGroupOptions(UseGroupName);
        setUseGroupOptionsDescriptions(UseGroupDescription)
      };
      fetchUseGroups();
    }, []);

    return (
      <FormikForm
        innerRef={ref} // Pass the forwarded ref to FormikForm
        allFields={allFields}
        buttonTitle={['Cancel']}
        content={<></>}
        heading={'Construction Permit'}
        buttonPlacement={'right'}
        formMethod={handleSubmit}
        handleOtherButton={() => {
          navigate(`/dashboard/${permit}`);
        }}
      />
    );
  }
);
// Add display name for debugging purposes
GeneralInformationForm.displayName = 'GeneralInformationForm';

export default GeneralInformationForm;
